import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { getMasterOptions } from '../../services/platform/masterService';

export interface MasterOption {
  _id: string;
  code: string;
  name: string;
}

interface Props {
  label: string;
  masterType: string;
  value: string;
  onChange: (code: string, option?: MasterOption) => void;
  filter?: { country?: string; state?: string };
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
  errorText?: string;
}

export default function SearchableMasterSelect({
  label, masterType, value, onChange, filter, required, disabled, helperText, error, errorText,
}: Props) {
  const { data: options = [], isLoading } = useQuery({
    queryKey: ['master-options', masterType, filter?.country, filter?.state],
    queryFn: () => getMasterOptions(masterType, filter),
    enabled: !!masterType,
  });

  const selected = useMemo(
    () => options.find((o) => o.code === value) || null,
    [options, value],
  );

  return (
    <Autocomplete
      size="small"
      disabled={disabled || isLoading}
      options={options}
      value={selected}
      getOptionLabel={(o) => `${o.name} (${o.code})`}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      onChange={(_, opt) => onChange(opt?.code || '', opt || undefined)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error || !!errorText}
          helperText={errorText || helperText || (isLoading ? 'Loading options…' : `Search ${label.toLowerCase()}`)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={isLoading ? 'Loading…' : `No ${label.toLowerCase()} — add parent masters first`}
    />
  );
}
